import { useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import styles from './styles.module.less';
import { TemplateDesigner } from './template-designer/template-designer';
import { UndoRedoHotkeys } from './undo-redo-hotkeys';
import { useDocumentTemplate } from './use-document-template';
import { useGetPreview } from './use-get-preview';
import { useUndoRedo } from './use-undo-redo';

import type { DocumentComponents, DocumentSize, DocumentTemplateType, PageDefinition } from './config';

type DocumentDesignerProps = {
  documentType: DocumentTemplateType;
  documentSize: DocumentSize;
};

type DocumentSnapshot = {
  page: PageDefinition;
  components: DocumentComponents;
};

export const DocumentDesigner = ({ documentType, documentSize }: DocumentDesignerProps) => {
  const { formatMessage } = useIntl();

  const {
    undo: undoHistory,
    redo: redoHistory,
    canUndo,
    canRedo,
    recordSnapshot,
    replaceHistory,
  } = useUndoRedo<DocumentSnapshot>({
    page: {
      data: {
        width: 0,
        height: 0,
        displayMode: 'grid',
      },
      ui: {
        documentSize: '8.5"x11"',
        gridCellSize: 12,
      },
    },
    components: [],
  });

  const documentTemplateOptions = useMemo(
    () => ({
      onLoad: (loadedPage: PageDefinition, loadedComponents: DocumentComponents) => {
        replaceHistory({
          page: loadedPage,
          components: loadedComponents,
        });
      },
    }),
    [replaceHistory],
  );

  const {
    page,
    updatePage: updatePageOriginal,
    components,
    setComponents: setComponentsOriginal,
    isLoading,
    saveDocument,
    isSaving,
    resetDocument,
  } = useDocumentTemplate(documentType, documentSize, documentTemplateOptions);

  const updatePage = useCallback(
    (updated: Partial<PageDefinition>) => {
      updatePageOriginal(updated);

      const updatedPage = {
        data: { ...page.data, ...updated.data },
        ui: { ...page.ui, ...updated.ui },
      };

      recordSnapshot({
        page: updatedPage,
        components,
      });
    },
    [updatePageOriginal, recordSnapshot, page, components],
  );

  const setComponents = useCallback(
    (newComponents: React.SetStateAction<DocumentComponents>) => {
      const updatedComponents = typeof newComponents === 'function' ? newComponents(components) : newComponents;
      setComponentsOriginal(newComponents);

      recordSnapshot({
        page,
        components: updatedComponents,
      });
    },
    [setComponentsOriginal, recordSnapshot, page, components],
  );

  const undo = useCallback(() => {
    const snapshot = undoHistory();
    if (snapshot) {
      updatePageOriginal(snapshot.page);
      setComponentsOriginal(snapshot.components);
    }
  }, [undoHistory, updatePageOriginal, setComponentsOriginal]);

  const redo = useCallback(() => {
    const snapshot = redoHistory();
    if (snapshot) {
      updatePageOriginal(snapshot.page);
      setComponentsOriginal(snapshot.components);
    }
  }, [redoHistory, updatePageOriginal, setComponentsOriginal]);

  const preview = useGetPreview(documentType, page, components);
  const isAnyOperationInProgress = isSaving || isLoading;

  return (
    <>
      <div className={styles['top-menu']}>
        <Text textType={TextType.H2} value={messages.documentDesigner} />
        <div className={styles['actions-container']}>
          <Button
            className={styles['action-button']}
            disabled={!canUndo || isAnyOperationInProgress}
            onClick={undo}
            theme={THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage {...messages.undo} />
          </Button>
          <Button
            className={styles['action-button']}
            disabled={!canRedo || isAnyOperationInProgress}
            onClick={redo}
            theme={THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage {...messages.redo} />
          </Button>
          <Button
            className={styles['action-button']}
            disabled={isAnyOperationInProgress}
            isLoading={isLoading}
            onClick={resetDocument}
            theme={THEME_CONSTANTS.tertiary}
          >
            <FormattedMessage {...messages.resetToDefault} />
          </Button>
          <Button
            className={styles['action-button']}
            disabled={isAnyOperationInProgress}
            isLoading={isSaving}
            onClick={saveDocument}
            theme={THEME_CONSTANTS.primary}
          >
            <FormattedMessage {...messages.saveTemplate} />
          </Button>
        </div>
      </div>
      <div className={styles['document-content']}>
        <div className={styles.designer}>
          <UndoRedoHotkeys canRedo={canRedo} canUndo={canUndo} onRedo={redo} onUndo={undo}>
            <TemplateDesigner components={components} page={page} setComponents={setComponents} updatePage={updatePage} />
          </UndoRedoHotkeys>
          <iframe
            className={styles.preview}
            src={preview}
            style={{
              width: page.data.width,
            }}
            title={formatMessage(messages.documentDesigner)}
          />
        </div>
      </div>
    </>
  );
};
